import { useState, useCallback } from 'react';
import { useTracking } from 'react-tracking';

import useUser from '@/hooks/useUser';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';
import { NarrativeFairValueModal } from '@/components/NarrativeFairValue';
import { useExistingNarrative } from '@/hooks/narrative/useExistingNarrative';
import GuestRegistrationModal from '@components/GuestRegistrationModal';

import { DefaultState } from './DefaultState';
import { PopulatedState } from './PopulatedState';

type Props = {
  companyId: string;
};

export const NarrativeKeyMetric = ({ companyId }: Props) => {
  const { trackEvent } = useTracking({ subject: 'primary_narrative' });
  const { data: user } = useUser();
  const { data: existingNarrative, status } = useExistingNarrative({
    companyId,
  });
  const [isNarrativeFairValueModalOpen, setNarrativeFairValueModal] =
    useState(false);
  const [isGuestRegistrationModalOpen, setGuestRegistrationModal] =
    useState(false);

  const onClick = useCallback(() => {
    if (user.loggedIn) {
      setNarrativeFairValueModal(true);
    } else {
      setGuestRegistrationModal(true);
    }
  }, [user.loggedIn]);

  if (status === 'pending' || status === 'error') {
    return <div>{/* Placeholder div to prevent CLS */}</div>;
  }

  const currencyISO = getTradingItemCurrencyISO(
    existingNarrative.Company.data.currencyInfo
  );
  const { analysisValue, myValuation } = existingNarrative.Company ?? {};

  return (
    <>
      {!myValuation ? (
        <DefaultState
          onClick={() => {
            trackEvent({ action: 'select' });
            onClick();
          }}
        />
      ) : (
        <PopulatedState
          currencyISO={currencyISO}
          lastSharePrice={analysisValue?.lastSharePrice ?? null}
          npvPerShare={analysisValue?.npvPerShare ?? null}
          fairValue={myValuation?.fairValue ?? null}
          onClick={() => {
            trackEvent({ action: 'edit' });
            onClick();
          }}
        />
      )}
      <NarrativeFairValueModal
        isModalOpen={isNarrativeFairValueModalOpen}
        onClose={() => setNarrativeFairValueModal(false)}
        companyId={companyId}
      />
      {isGuestRegistrationModalOpen && (
        <GuestRegistrationModal
          canClose
          onClose={() => setGuestRegistrationModal(false)}
          removeWindowScroll
          onLoginSuccess={() => setGuestRegistrationModal(false)}
          onRegisterSuccess={() => setGuestRegistrationModal(false)}
        />
      )}
    </>
  );
};
